import { z } from "zod";
import { isoDate, isoDateTime, optionalText, text, uuid } from "./common";

/*
 * Clinical record (Phase 3): visits, versioned clinical notes, diagnoses, medical history, allergies,
 * patient medications, clinical summary and the Patient 360 timeline.
 */

export const NOTE_TYPES = ["consultation", "procedure", "follow_up", "phone", "referral", "other"] as const;
export type NoteType = (typeof NOTE_TYPES)[number];
export const NOTE_STATUSES = ["draft", "signed", "amended"] as const;
export type NoteStatus = (typeof NOTE_STATUSES)[number];

export const DIAGNOSIS_STATUSES = ["provisional", "confirmed", "resolved", "ruled_out", "entered_in_error"] as const;
export type DiagnosisStatus = (typeof DIAGNOSIS_STATUSES)[number];
export const DIAGNOSIS_CODE_SYSTEMS = ["ICD-10", "ICD-11", "ICD-11-TM2", "NAMASTE", "SNODENT", "SNOMED-CT", "local"] as const;
export const HISTORY_CODE_SYSTEMS = ["ICD-10", "ICD-11", "SNOMED-CT", "local"] as const;
const SURFACES = ["M", "O", "D", "B", "L", "I", "P"] as const;

export const MEDICAL_HISTORY_STATUSES = ["active", "controlled", "resolved", "unknown", "entered_in_error"] as const;
export const ALLERGEN_TYPES = ["drug", "food", "latex", "environmental", "material", "other"] as const;
export const ALLERGY_SEVERITIES = ["mild", "moderate", "severe", "life_threatening", "unknown"] as const;
export const ALLERGY_STATUSES = ["active", "inactive", "entered_in_error"] as const;
export const MEDICATION_STATUSES = ["active", "stopped", "entered_in_error"] as const;
export const MEDICATION_SOURCES = ["patient_reported", "external_prescription", "this_clinic"] as const;

const version = z.number().int().min(1);

/** Optional long text for PATCH-like inputs: undefined = keep, "" or null = clear. */
const patchText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .optional()
    .transform((v) => (v === undefined ? undefined : v ? v : null));

const toothCode = z
  .string()
  .regex(/^([1-4][1-8]|[5-8][1-5])$/, "Use an FDI tooth number (11–48 permanent, 51–85 primary)")
  .nullable()
  .default(null);

/* ------------------------------------------------------------------------------------------ visits */

export const createVisitInput = z.object({
  branchId: uuid,
  /** Defaults to the signed-in doctor. Required when the user is not a doctor. */
  doctorId: uuid.optional(),
  chiefComplaint: optionalText(2000),
});

export const updateVisitInput = z.object({
  version,
  chiefComplaint: optionalText(2000),
});

export const closeVisitInput = z.object({ version });

export const clinicalListQuery = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().max(300).optional(),
});

export interface VisitListItem {
  id: string;
  status: "open" | "closed";
  startedAt: string;
  endedAt: string | null;
  chiefComplaint: string | null;
  doctor: { id: string; fullName: string };
  branch: { id: string; name: string };
  appointment: { id: string; startsAt: string; status: string } | null;
  noteCount: number;
  procedureCount: number;
  version: number;
}

export interface TreatmentSessionSummary {
  id: string;
  service: { id: string; name: string };
  toothCode: string | null;
  surfaces: string[];
  doctor: { id: string; fullName: string };
  performedAt: string;
  status: string;
  stageLabel: string | null;
  notes: string | null;
}

export interface VisitDetail extends VisitListItem {
  patient: { id: string; fullName: string; patientCode: string };
  notes: ClinicalNote[];
  diagnoses: Diagnosis[];
  treatmentSessions: TreatmentSessionSummary[];
}

/* ----------------------------------------------------------------------------------- clinical notes */

const noteFieldShape = {
  chiefComplaint: patchText(4000),
  history: patchText(20000),
  examination: patchText(20000),
  assessment: patchText(20000),
  plan: patchText(20000),
  doctorNotes: patchText(20000),
};
export const NOTE_FIELDS = ["chiefComplaint", "history", "examination", "assessment", "plan", "doctorNotes"] as const;
export type NoteField = (typeof NOTE_FIELDS)[number];

export const createNoteInput = z.object({
  noteType: z.enum(NOTE_TYPES).default("consultation"),
  visitId: uuid.nullable().default(null),
  /** The responsible doctor. Defaults to the signed-in doctor; required when the author is not a doctor. */
  doctorId: uuid.optional(),
  ...noteFieldShape,
});

export const updateNoteDraftInput = z.object({
  version,
  ...noteFieldShape,
  /** Only for amendment drafts (version 2+). */
  amendmentReason: patchText(2000),
});

export const signNoteInput = z.object({ version });

export const amendNoteInput = z.object({
  version,
  amendmentReason: text(2000),
  ...noteFieldShape,
});

export interface ClinicalNoteVersion {
  id: string;
  versionNo: number;
  chiefComplaint: string | null;
  history: string | null;
  examination: string | null;
  assessment: string | null;
  plan: string | null;
  doctorNotes: string | null;
  amendmentReason: string | null;
  author: { id: string; fullName: string };
  signedAt: string | null;
  signedBy: { id: string; fullName: string } | null;
  aiDraftId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ClinicalNote {
  id: string;
  patientId: string;
  visitId: string | null;
  noteType: NoteType;
  status: NoteStatus;
  doctor: { id: string; fullName: string };
  currentVersionNo: number;
  firstSignedAt: string | null;
  /** version_no of the unsigned draft, if one exists. */
  draftVersionNo: number | null;
  /** The latest signed version (or the draft, for a note never signed). */
  current: ClinicalNoteVersion;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface ClinicalNoteDetail extends ClinicalNote {
  /** All versions, oldest first. Signed versions are frozen. */
  versions: ClinicalNoteVersion[];
}

/* -------------------------------------------------------------------------------------- diagnoses */

export const createDiagnosisInput = z
  .object({
    visitId: uuid.nullable().default(null),
    doctorId: uuid.nullable().optional(),
    codeSystem: z.enum(DIAGNOSIS_CODE_SYSTEMS).nullable().default(null),
    code: optionalText(40),
    description: text(500),
    toothCode,
    surfaces: z.array(z.enum(SURFACES)).max(7).default([]),
    status: z.enum(["provisional", "confirmed"]).default("confirmed"),
    diagnosedAt: isoDateTime.optional(),
    notes: optionalText(2000),
  })
  .refine((d) => (d.codeSystem === null) === (d.code === null), { message: "Give both a code system and a code, or neither", path: ["code"] })
  .refine((d) => d.surfaces.length === 0 || d.toothCode !== null, { message: "Surfaces need a tooth", path: ["surfaces"] });

export const updateDiagnosisInput = z.object({
  status: z.enum(DIAGNOSIS_STATUSES).optional(),
  description: text(500).optional(),
  notes: patchText(2000),
  /** Required when marking entered_in_error. */
  reason: optionalText(500),
});

export interface Diagnosis {
  id: string;
  patientId: string;
  visitId: string | null;
  doctor: { id: string; fullName: string } | null;
  codeSystem: string | null;
  code: string | null;
  description: string;
  toothCode: string | null;
  surfaces: string[];
  status: DiagnosisStatus;
  diagnosedAt: string;
  resolvedAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export const clinicalRecordListQuery = z.object({
  /** entered_in_error rows are hidden unless asked for. */
  includeErrors: z
    .enum(["true", "false"])
    .default("false")
    .transform((v) => v === "true"),
});

/* ------------------------------------------------------------------------------- medical history */

export const createMedicalHistoryInput = z
  .object({
    conditionName: text(200),
    codeSystem: z.enum(HISTORY_CODE_SYSTEMS).nullable().default(null),
    code: optionalText(40),
    onsetDate: isoDate.nullable().default(null),
    onsetNote: optionalText(200),
    status: z.enum(MEDICAL_HISTORY_STATUSES).default("active"),
    isCritical: z.boolean().default(false),
    notes: optionalText(2000),
  })
  .refine((d) => (d.codeSystem === null) === (d.code === null), { message: "Give both a code system and a code, or neither", path: ["code"] });

export const updateMedicalHistoryInput = z.object({
  conditionName: text(200).optional(),
  onsetDate: isoDate.nullable().optional(),
  onsetNote: patchText(200),
  status: z.enum(MEDICAL_HISTORY_STATUSES).optional(),
  isCritical: z.boolean().optional(),
  notes: patchText(2000),
});

export interface MedicalHistoryItem {
  id: string;
  patientId: string;
  conditionName: string;
  codeSystem: string | null;
  code: string | null;
  onsetDate: string | null;
  onsetNote: string | null;
  status: (typeof MEDICAL_HISTORY_STATUSES)[number];
  isCritical: boolean;
  notes: string | null;
  verifiedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/* -------------------------------------------------------------------------------------- allergies */

export const createAllergyInput = z.object({
  allergen: text(200),
  allergenType: z.enum(ALLERGEN_TYPES).default("drug"),
  reaction: optionalText(500),
  severity: z.enum(ALLERGY_SEVERITIES).default("unknown"),
  status: z.enum(ALLERGY_STATUSES).default("active"),
  notedOn: isoDate.nullable().default(null),
  notes: optionalText(2000),
});

export const updateAllergyInput = z.object({
  allergen: text(200).optional(),
  allergenType: z.enum(ALLERGEN_TYPES).optional(),
  reaction: patchText(500),
  severity: z.enum(ALLERGY_SEVERITIES).optional(),
  status: z.enum(ALLERGY_STATUSES).optional(),
  notedOn: isoDate.nullable().optional(),
  notes: patchText(2000),
});

export interface Allergy {
  id: string;
  patientId: string;
  allergen: string;
  allergenType: (typeof ALLERGEN_TYPES)[number];
  reaction: string | null;
  severity: (typeof ALLERGY_SEVERITIES)[number];
  status: (typeof ALLERGY_STATUSES)[number];
  notedOn: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/* ---------------------------------------------------------------------------- patient medications */

export const createPatientMedicationInput = z.object({
  medicineId: uuid.nullable().default(null),
  medicineName: text(200),
  dose: optionalText(100),
  frequency: optionalText(100),
  route: optionalText(50),
  indication: optionalText(200),
  startedOn: isoDate.nullable().default(null),
  stoppedOn: isoDate.nullable().default(null),
  status: z.enum(MEDICATION_STATUSES).default("active"),
  source: z.enum(MEDICATION_SOURCES).default("patient_reported"),
  notes: optionalText(2000),
});

export const updatePatientMedicationInput = z.object({
  medicineName: text(200).optional(),
  dose: patchText(100),
  frequency: patchText(100),
  route: patchText(50),
  indication: patchText(200),
  startedOn: isoDate.nullable().optional(),
  stoppedOn: isoDate.nullable().optional(),
  status: z.enum(MEDICATION_STATUSES).optional(),
  source: z.enum(MEDICATION_SOURCES).optional(),
  notes: patchText(2000),
});

export interface PatientMedication {
  id: string;
  patientId: string;
  medicineId: string | null;
  medicineName: string;
  dose: string | null;
  frequency: string | null;
  route: string | null;
  indication: string | null;
  startedOn: string | null;
  stoppedOn: string | null;
  status: (typeof MEDICATION_STATUSES)[number];
  source: (typeof MEDICATION_SOURCES)[number];
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/* ------------------------------------------------------------------------------ clinical summary */

export interface ClinicalAlert {
  kind: "condition" | "allergy";
  id: string;
  label: string;
  /** Allergy severity, or "critical" for a critical medical condition. */
  severity: string;
  detail: string | null;
}

export interface ClinicalSummary {
  alerts: ClinicalAlert[];
  activeDiagnoses: Diagnosis[];
  medications: PatientMedication[];
  lastVisit: { id: string; startedAt: string; status: "open" | "closed"; chiefComplaint: string | null; doctor: { id: string; fullName: string } } | null;
  openDraftNotes: number;
}

/* ---------------------------------------------------------------------------- Patient 360 timeline */

export const TIMELINE_KINDS = [
  "appointment",
  "visit",
  "note",
  "diagnosis",
  "chart_entry",
  "treatment_plan",
  "treatment_session",
  "prescription",
  "document",
  "consent",
] as const;
export type TimelineKind = (typeof TIMELINE_KINDS)[number];

export const timelineQuery = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(30),
  before: z.string().max(300).optional(),
});

export interface TimelineItem {
  /** Unique per event (one record can produce several events, e.g. plan created / accepted). */
  id: string;
  kind: TimelineKind;
  at: string;
  title: string;
  subtitle: string | null;
  status: string | null;
  ref: { type: string; id: string };
}
